// bluaIQ — WEBSITE-TAKEOFF-PML-PREVIEW-0: static fixture for the read-only
// Takeoff-to-Project-Material-List evidence preview.
//
// DEMO / READ-ONLY ONLY. Every item below is a hand-written literal that MIRRORS the
// already-accepted TAKEOFF diagnostic artifacts (TAKEOFF-PML-READONLY-BRIDGE-0,
// TAKEOFF-VERIFICATION-PATHS-0, TAKEOFF-P2-SOURCE-DIMENSION-RECOVERY-1,
// TAKEOFF-P2-DIMENSION-LINKAGE-2). It is a fixed snapshot: it is NOT produced by any
// AI system, OCR, PDF parser, or measurement engine, it does NOT read the TAKEOFF repo
// at runtime, it reaches no backend, and it creates nothing anywhere.
//
// HARD POSTURE (encoded on every item): quantity is always null; nothing is verified;
// nothing is a confirmed dimension; nothing is ready to order; human review is required
// before any downstream write. No BOM, no BOQ, no purchase quantity, and no Material
// Order is ever created here. `(16" O.C.)` is spacing context only — never a quantity
// and never a confirmed dimension; the run-length-shaped tokens 16'-7" / 16'-0" are
// weak/negative linkage for furring, never a furring run length and never a quantity
// basis. No record is ever silently dropped: all 15 preserved records are represented.

import type { HandoffAssumption, HandoffBlocker } from '../../shared/integrationHandoffContracts'

/** Distinct source marker for this preview — NOT a ProjectMaterialSourceModule value. */
export const TAKEOFF_PML_PREVIEW_SOURCE_MODULE = 'takeoff_accepted_artifact_preview' as const

/**
 * Canonical identity of the preserved AI Draft Takeoff artifact. Per the TAKEOFF track,
 * the sourceDocumentHash (never a filename/task label) is identity. This preview mirrors
 * that hash so a human can trace every line back to the accepted artifact.
 */
export const TAKEOFF_PML_PREVIEW_SOURCE_DOCUMENT_HASH =
  '90ac0e83d5e4cae3c210c544b5d003609f90cc790f257112bcc47e46af3d3125' as const

/** The four accepted TAKEOFF artifacts this preview represents (read-only). */
export const TAKEOFF_PML_PREVIEW_ACCEPTED_ARTIFACTS = Object.freeze([
  'TAKEOFF-PML-READONLY-BRIDGE-0',
  'TAKEOFF-VERIFICATION-PATHS-0',
  'TAKEOFF-P2-SOURCE-DIMENSION-RECOVERY-1',
  'TAKEOFF-P2-DIMENSION-LINKAGE-2',
] as const)

/** Preview trust state — only ever `needs_review` or `blocked`; never `verified`. */
export type TakeoffPreviewQuantityStatus = 'needs_review' | 'blocked'

/** Where each preserved record sits on its verification path (guidance only). */
export type TakeoffPreviewLinkageStatus =
  | 'assisted_capture_pending'
  | 'opening_dimension_linkage_gated'
  | 'unresolved_no_dimension_candidate_recovered'
  | 'linkage_evidence_expanded_review_required'
  | 'assumption_requires_review'

export interface TakeoffPreviewProvenanceRef {
  /** A source evidence ref copied verbatim from the accepted artifact (ids/labels only). */
  artifactRef: string
  note?: string | null
}

/** One classified evidence token for the P2 furring record (0011) — nothing confirmed. */
export interface TakeoffPreviewTokenClassification {
  token: string
  page: string
  classification: 'spacing_context_candidate' | 'weak_or_negative_linkage_for_furring'
  /** Spacing/dimension tokens are NEVER a quantity in this preview. */
  isQuantity: false
  /** Nothing here is a confirmed dimension. */
  isConfirmedDimension: false
  note: { en: string; es: string }
}

/** Deterministic (non-AI) source-dimension linkage evidence for record 0011. */
export interface TakeoffPreviewP2Evidence {
  recoveredPhrase: string
  recoveredPhrasePage: string
  /** The `(16" O.C.)` context is spacing only. */
  spacingContextOnly: true
  /** No run-length dimension was confirmed. */
  runLengthDimensionConfirmed: false
  tokens: TakeoffPreviewTokenClassification[]
}

/**
 * One preserved TAKEOFF line, rendered read-only. `quantity` is always null, `verified`
 * always false, `notReadyToOrder`/`humanReviewRequired`/`reviewOnly` always true, and
 * `noSilentDrop` attests the record is represented (not silently dropped).
 */
export interface TakeoffPmlPreviewItem {
  sourceModule: typeof TAKEOFF_PML_PREVIEW_SOURCE_MODULE
  sourceArtifactIds: string[]
  sourceDocumentHash: typeof TAKEOFF_PML_PREVIEW_SOURCE_DOCUMENT_HASH
  recordId: string
  itemName: string
  stage: string
  category: string
  /** True for the 11 source-backed spec lines, false for the 4 assumption/blocked lines. */
  sourceBacked: boolean
  quantity: null
  quantityStatus: TakeoffPreviewQuantityStatus
  /** The accepted artifact's confidence token (e.g. medium_source_backed_partial). */
  confidenceBasis: string
  verified: false
  notReadyToOrder: true
  humanReviewRequired: true
  reviewOnly: true
  provenanceRefs: TakeoffPreviewProvenanceRef[]
  blockers: HandoffBlocker[]
  assumptions: HandoffAssumption[]
  /** Per-record attestation: this record is represented (not silently dropped). */
  noSilentDrop: true
  linkageStatus: TakeoffPreviewLinkageStatus
  reviewReason: { en: string; es: string }
  /** Present only on the P2 furring record (0011). */
  p2Evidence?: TakeoffPreviewP2Evidence
}

const BRIDGE_AND_PATHS = [
  'TAKEOFF-PML-READONLY-BRIDGE-0',
  'TAKEOFF-VERIFICATION-PATHS-0',
]

/** Build one fully-shaped preview item with every safety field forced on (none can be
 *  silently omitted). `n` becomes the zero-padded record suffix (0001..0015). */
function mk(seed: {
  n: number
  itemName: string
  stage: string
  category: string
  sourceBacked: boolean
  quantityStatus: TakeoffPreviewQuantityStatus
  confidenceBasis: string
  linkageStatus: TakeoffPreviewLinkageStatus
  sourceArtifactIds: string[]
  provenanceRefs: TakeoffPreviewProvenanceRef[]
  blockers: HandoffBlocker[]
  assumptions: HandoffAssumption[]
  reviewReason: { en: string; es: string }
  p2Evidence?: TakeoffPreviewP2Evidence
}): TakeoffPmlPreviewItem {
  return {
    sourceModule: TAKEOFF_PML_PREVIEW_SOURCE_MODULE,
    sourceArtifactIds: seed.sourceArtifactIds,
    sourceDocumentHash: TAKEOFF_PML_PREVIEW_SOURCE_DOCUMENT_HASH,
    recordId: `ai-draft-construction-stage-takeoff-${String(seed.n).padStart(4, '0')}`,
    itemName: seed.itemName,
    stage: seed.stage,
    category: seed.category,
    sourceBacked: seed.sourceBacked,
    quantity: null,
    quantityStatus: seed.quantityStatus,
    confidenceBasis: seed.confidenceBasis,
    verified: false,
    notReadyToOrder: true,
    humanReviewRequired: true,
    reviewOnly: true,
    provenanceRefs: seed.provenanceRefs,
    blockers: seed.blockers,
    assumptions: seed.assumptions,
    noSilentDrop: true,
    linkageStatus: seed.linkageStatus,
    reviewReason: seed.reviewReason,
    ...(seed.p2Evidence ? { p2Evidence: seed.p2Evidence } : {}),
  }
}

// Shared blocker/review templates (kept faithful to the accepted artifact's posture).
const MEASURED_GEOMETRY_BLOCKER: HandoffBlocker = {
  code: 'missing_measured_geometry',
  message: 'Quantity requires assisted or manual measured-geometry capture — preliminary only',
  severity: 'WARNING',
  isBlocking: false,
}
const ASSISTED_CAPTURE_REVIEW = {
  en: 'Source spec present, measured geometry missing — assisted capture required before any quantity.',
  es: 'Especificación de origen presente, geometría medida faltante — se requiere captura asistida antes de cualquier cantidad.',
}

/**
 * All 15 preserved AI Draft Takeoff records, mirrored as read-only preview items.
 * 11 source-backed spec lines (0001–0011) + 4 assumption/blocked lines (0012–0015).
 */
export const takeoffPmlPreviewItems: TakeoffPmlPreviewItem[] = [
  mk({
    n: 1,
    itemName: 'Hardie lap siding 8.25',
    stage: 'Exterior Siding / Trim / Cement Board',
    category: 'siding',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'opening_dimension_linkage_gated',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:HARDIE LAP SIDING 8.25', note: 'Elevation material spec candidate (source-backed).' },
    ],
    blockers: [
      {
        code: 'opening_dimension_linkage_gated',
        message: 'Opening-dimension linkage is gated behind the paused opening-symbol blocker chain — quantity cannot be derived yet',
        severity: 'BLOCKING',
        isBlocking: true,
      },
    ],
    assumptions: [],
    reviewReason: {
      en: 'Source spec present, quantity missing — opening-dimension linkage is gated (paused opening-symbol blocker chain).',
      es: 'Especificación de origen presente, cantidad faltante — la vinculación de dimensión de abertura está bloqueada (cadena de bloqueo de símbolo de abertura en pausa).',
    },
  }),
  mk({
    n: 2,
    itemName: 'Composition roof',
    stage: 'Roof Framing / Roof System',
    category: 'roofing',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00003:COMPOSITION ROOF', note: 'Elevation material spec candidate (review required).' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 3,
    itemName: 'Gutters',
    stage: 'Openings / Windows / Doors / Gutters',
    category: 'gutters',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'unresolved_no_dimension_candidate_recovered',
    sourceArtifactIds: [...BRIDGE_AND_PATHS, 'TAKEOFF-P2-SOURCE-DIMENSION-RECOVERY-1'],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00004:GUTTERS' },
      { artifactRef: 'pdf-reading-v2-page-00005:GUTTERS' },
    ],
    blockers: [
      {
        code: 'missing_source_dimension',
        message: 'No source dimension candidate recovered — unresolved, lower confidence',
        severity: 'WARNING',
        isBlocking: false,
      },
    ],
    assumptions: [],
    reviewReason: {
      en: 'Source spec present, source dimension missing — no dimension candidate recovered (unresolved, lower confidence).',
      es: 'Especificación de origen presente, dimensión de origen faltante — no se recuperó ningún candidato de dimensión (sin resolver, menor confianza).',
    },
  }),
  mk({
    n: 4,
    itemName: 'ZIP sheathing',
    stage: 'Exterior Sheathing / WRB',
    category: 'sheathing',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:ZIP-SHEATHING', note: 'Elevation material spec candidate (source-backed).' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 5,
    itemName: 'R-13 fiberglass insulation',
    stage: 'Insulation',
    category: 'insulation',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:R-13 FIBERGLASS INSULATION' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 6,
    itemName: 'R-19 insulation',
    stage: 'Insulation',
    category: 'insulation',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:R-19 INSULATION' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 7,
    itemName: 'Drywall',
    stage: 'Drywall / Interior Board',
    category: 'drywall',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:DRYWALL' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 8,
    itemName: 'Cement board panel',
    stage: 'Exterior Siding / Trim / Cement Board',
    category: 'cement_board',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:CEMENT BOARD PANEL' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 9,
    itemName: '3:12 roof pitch',
    stage: 'Roof Framing / Roof System',
    category: 'roofing',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'assisted_capture_pending',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00003:3:12 ROOF PITCH' },
    ],
    blockers: [MEASURED_GEOMETRY_BLOCKER],
    assumptions: [],
    reviewReason: ASSISTED_CAPTURE_REVIEW,
  }),
  mk({
    n: 10,
    itemName: 'Floor truss',
    stage: 'Framing / Structural',
    category: 'framing_structural',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'unresolved_no_dimension_candidate_recovered',
    sourceArtifactIds: [...BRIDGE_AND_PATHS, 'TAKEOFF-P2-SOURCE-DIMENSION-RECOVERY-1'],
    provenanceRefs: [
      { artifactRef: 'pdf-reading-v2-page-00006:FLOOR TRUSS' },
    ],
    blockers: [
      {
        code: 'missing_source_dimension',
        message: 'No source dimension candidate recovered — unresolved, lower confidence',
        severity: 'WARNING',
        isBlocking: false,
      },
    ],
    assumptions: [],
    reviewReason: {
      en: 'Source spec present, source dimension missing — no dimension candidate recovered (unresolved, lower confidence).',
      es: 'Especificación de origen presente, dimensión de origen faltante — no se recuperó ningún candidato de dimensión (sin resolver, menor confianza).',
    },
  }),
  mk({
    n: 11,
    itemName: '3/4" furring strip at 16" O.C.',
    stage: 'Framing / Structural',
    category: 'framing_structural',
    sourceBacked: true,
    quantityStatus: 'needs_review',
    confidenceBasis: 'medium_source_backed_partial',
    linkageStatus: 'linkage_evidence_expanded_review_required',
    sourceArtifactIds: [...BRIDGE_AND_PATHS, 'TAKEOFF-P2-SOURCE-DIMENSION-RECOVERY-1', 'TAKEOFF-P2-DIMENSION-LINKAGE-2'],
    provenanceRefs: [
      { artifactRef: `pdf_reading_v2_substrate:pdf-reading-v2-page-00006:FURRING`, note: 'Same-line phrase token (page 00006).' },
      { artifactRef: `pdf_reading_v2_substrate:pdf-reading-v2-page-00006:STRIP`, note: 'Same-line phrase token (page 00006).' },
      { artifactRef: `pdf_reading_v2_substrate:pdf-reading-v2-page-00006:(16"`, note: 'Spacing context token (page 00006).' },
      { artifactRef: `pdf_reading_v2_substrate:pdf-reading-v2-page-00001:16'-7"`, note: 'Weak/negative linkage — site/driveway context (page 00001).' },
      { artifactRef: `pdf_reading_v2_substrate:pdf-reading-v2-page-00002:16'-0"`, note: 'Weak/negative linkage — interior room context (page 00002).' },
    ],
    blockers: [
      {
        code: 'missing_source_dimension',
        message: 'Source dimension evidence expanded; spacing context only, no confirmed run length — review required',
        severity: 'WARNING',
        isBlocking: false,
      },
    ],
    assumptions: [],
    reviewReason: {
      en: 'Source dimension evidence expanded — spacing context only; run-length dimension not confirmed. Review required.',
      es: 'Evidencia de dimensión de origen ampliada — solo contexto de espaciamiento; dimensión de longitud no confirmada. Se requiere revisión.',
    },
    p2Evidence: {
      recoveredPhrase: '3/4" FURRING STRIP (16" O.C.)',
      recoveredPhrasePage: 'pdf-reading-v2-page-00006',
      spacingContextOnly: true,
      runLengthDimensionConfirmed: false,
      tokens: [
        {
          token: '(16" O.C.)',
          page: 'pdf-reading-v2-page-00006',
          classification: 'spacing_context_candidate',
          isQuantity: false,
          isConfirmedDimension: false,
          note: {
            en: '16" O.C. is spacing only — not a quantity and not a confirmed dimension.',
            es: '16" O.C. es solo espaciamiento — no es una cantidad ni una dimensión confirmada.',
          },
        },
        {
          token: `16'-7"`,
          page: 'pdf-reading-v2-page-00001',
          classification: 'weak_or_negative_linkage_for_furring',
          isQuantity: false,
          isConfirmedDimension: false,
          note: {
            en: 'Driveway / site-plan context — not a furring run length, not a quantity basis.',
            es: 'Contexto de entrada de auto / plano de sitio — no es una longitud de tira de furring ni una base de cantidad.',
          },
        },
        {
          token: `16'-0"`,
          page: 'pdf-reading-v2-page-00002',
          classification: 'weak_or_negative_linkage_for_furring',
          isQuantity: false,
          isConfirmedDimension: false,
          note: {
            en: 'Kitchen / interior-room context — not a furring run length, not a quantity basis.',
            es: 'Contexto de cocina / habitación interior — no es una longitud de tira de furring ni una base de cantidad.',
          },
        },
      ],
    },
  }),
  mk({
    n: 12,
    itemName: 'Site / general conditions scope',
    stage: 'Site / General Conditions if applicable',
    category: 'site_general_conditions',
    sourceBacked: false,
    quantityStatus: 'blocked',
    confidenceBasis: 'blocked_unknown',
    linkageStatus: 'assumption_requires_review',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'ai-draft-construction-stage-takeoff-0012:assumption_basis', note: 'Construction-stage bucket retained; no source-backed evidence converted.' },
    ],
    blockers: [
      {
        code: 'assumption_requires_review',
        message: 'No source-backed quantity evidence — construction-stage bucket retained; human decision required before any quantity',
        severity: 'BLOCKING',
        isBlocking: true,
      },
    ],
    assumptions: [
      {
        id: 'ai-draft-construction-stage-takeoff-0012-assumption',
        statement: 'Required construction-stage bucket retained, but no source-backed material/quantity line was converted by the strict verified pipeline.',
        impactIfWrong: 'A quantity could be fabricated without source evidence',
        mustConfirm: true,
      },
    ],
    reviewReason: {
      en: 'No source-backed quantity evidence — construction-stage bucket retained; assumption requires human review.',
      es: 'Sin evidencia de cantidad respaldada por origen — bloque de etapa de construcción conservado; el supuesto requiere revisión humana.',
    },
  }),
  mk({
    n: 13,
    itemName: 'Foundation / slab scope',
    stage: 'Foundation / Slab',
    category: 'foundation_slab',
    sourceBacked: false,
    quantityStatus: 'blocked',
    confidenceBasis: 'blocked_unknown',
    linkageStatus: 'assumption_requires_review',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'ai-draft-construction-stage-takeoff-0013:assumption_basis', note: 'Construction-stage bucket retained; no source-backed evidence converted.' },
    ],
    blockers: [
      {
        code: 'assumption_requires_review',
        message: 'No source-backed quantity evidence — construction-stage bucket retained; human decision required before any quantity',
        severity: 'BLOCKING',
        isBlocking: true,
      },
    ],
    assumptions: [
      {
        id: 'ai-draft-construction-stage-takeoff-0013-assumption',
        statement: 'Required construction-stage bucket retained, but no source-backed foundation/slab quantity evidence was converted by the strict verified pipeline.',
        impactIfWrong: 'A quantity could be fabricated without source evidence',
        mustConfirm: true,
      },
    ],
    reviewReason: {
      en: 'No source-backed quantity evidence — construction-stage bucket retained; assumption requires human review.',
      es: 'Sin evidencia de cantidad respaldada por origen — bloque de etapa de construcción conservado; el supuesto requiere revisión humana.',
    },
  }),
  mk({
    n: 14,
    itemName: 'Interior trim / baseboard scope',
    stage: 'Interior Trim / Baseboard if evidence exists',
    category: 'interior_trim_baseboard',
    sourceBacked: false,
    quantityStatus: 'blocked',
    confidenceBasis: 'blocked_unknown',
    linkageStatus: 'assumption_requires_review',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'ai-draft-construction-stage-takeoff-0014:assumption_basis', note: 'Stage retained as conditional; no baseboard evidence converted.' },
    ],
    blockers: [
      {
        code: 'assumption_requires_review',
        message: 'No source-backed quantity evidence — construction-stage bucket retained; human decision required before any quantity',
        severity: 'BLOCKING',
        isBlocking: true,
      },
    ],
    assumptions: [
      {
        id: 'ai-draft-construction-stage-takeoff-0014-assumption',
        statement: 'Stage retained as conditional because no baseboard LF evidence was converted into a verified or draft quantity candidate.',
        impactIfWrong: 'A quantity could be fabricated without source evidence',
        mustConfirm: true,
      },
    ],
    reviewReason: {
      en: 'No source-backed quantity evidence — construction-stage bucket retained; assumption requires human review.',
      es: 'Sin evidencia de cantidad respaldada por origen — bloque de etapa de construcción conservado; el supuesto requiere revisión humana.',
    },
  }),
  mk({
    n: 15,
    itemName: 'Other finish/source-backed notes scope',
    stage: 'Finish / Other source-backed notes if applicable',
    category: 'finish_other',
    sourceBacked: false,
    quantityStatus: 'blocked',
    confidenceBasis: 'blocked_unknown',
    linkageStatus: 'assumption_requires_review',
    sourceArtifactIds: [...BRIDGE_AND_PATHS],
    provenanceRefs: [
      { artifactRef: 'ai-draft-construction-stage-takeoff-0015:assumption_basis', note: 'Stage retained for completeness; no additional finish quantity created.' },
    ],
    blockers: [
      {
        code: 'assumption_requires_review',
        message: 'No source-backed quantity evidence — construction-stage bucket retained; human decision required before any quantity',
        severity: 'BLOCKING',
        isBlocking: true,
      },
    ],
    assumptions: [
      {
        id: 'ai-draft-construction-stage-takeoff-0015-assumption',
        statement: 'Stage retained for completeness; no additional source-backed finish quantity is created in this draft.',
        impactIfWrong: 'A quantity could be fabricated without source evidence',
        mustConfirm: true,
      },
    ],
    reviewReason: {
      en: 'No source-backed quantity evidence — construction-stage bucket retained; assumption requires human review.',
      es: 'Sin evidencia de cantidad respaldada por origen — bloque de etapa de construcción conservado; el supuesto requiere revisión humana.',
    },
  }),
]

/** The furring record that carries the P2 dimension-linkage evidence (record 0011). */
export const TAKEOFF_PML_PREVIEW_FURRING_RECORD_ID =
  'ai-draft-construction-stage-takeoff-0011' as const

/** Convenience export so callers can assert record coverage without recomputing it. */
export const TAKEOFF_PML_PREVIEW_RECORD_IDS: string[] =
  takeoffPmlPreviewItems.map((i) => i.recordId)

/**
 * No-silent-drop proof for the whole preview: all 15 preserved records are represented,
 * none dropped. Mirrors the accepted artifacts' no-silent-drop posture.
 */
export const takeoffPmlPreviewNoSilentDrop = {
  totalRecords: takeoffPmlPreviewItems.length,
  representedRecords: takeoffPmlPreviewItems.length,
  droppedRecords: [] as string[],
} as const
